// ────────────────────────────────────────────────────────────────────
// AppShell — wraps every authenticated route.
//
// Layout:
//   ┌─ Top header (logo + nav + user) ────────────────────┐
//   │ ─── tick row ───────────────────────────────────────│
//   │ sub-status: SYS · TENANT · PERIOD · standards · UTC │
//   ├─────────────────────────────────────────────────────┤
//   │             <Outlet> (page content)                 │
//   └─────────────────────────────────────────────────────┘
// ────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, FileText, Settings, Wrench, LogOut, Bell, PanelLeft } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Logo } from "./Logo";
import { CommandSearch } from "./CommandSearch";
import { cn } from "@/lib/utils";
import { getCachedUser, clearToken, getToken, studiosHandoffUrl, STAFF_ROLES,
         apiClient, getActiveTenant, setActiveTenant, tenantClearedOnPurpose } from "@/lib/api";
import type { Client, UserOut } from "@/lib/types";
import { WorkspaceProvider } from "./WorkspaceContext";
import { WorklistProvider } from "./WorklistContext";
import { ModsDockMounter } from "./ChatDock";
import { TeamChatDockMounter, openChatForTenant } from "./TeamChatDock";
import { NotesDockMounter } from "./NotesDock";
import { PresenceBeacon } from "@/lib/presence";
import { DockProvider, DockBar } from "./dock";
import { ValidationCaveat } from "./ValidationCaveat";
import { LeftTree } from "./shell/LeftTree";
import { visibleSlots, promotedNav } from "./shell/navItems";
import { useIsNarrow } from "@/lib/useIsNarrow";

// Staff "view as tenant" — pick which customer's data the portal shows.
function TenantSwitcher(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const current = getActiveTenant();
  useEffect(() => { apiClient.clients().then(setClients).catch(() => undefined); }, []);
  const currentName = clients.find((c) => String(c.id) === current)?.name;
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-1.5 hover:text-ink-2 transition-colors">
        <span>TENANT · {currentName ? currentName.toUpperCase() : <span className="text-accent-deep">SELECT</span>}</span>
        {/* points up: the menu opens upward from this bottom bar */}
        <ChevronDown size={10} className={cn("transition-transform duration-150", !open && "rotate-180")} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[1090]" onClick={() => setOpen(false)} />
          <div className="absolute left-0 bottom-6 z-[1100] min-w-[240px] bg-panel-900 border border-panel-700 shadow-xl py-1 normal-case tracking-normal">
            {clients.length === 0 && <div className="px-3 py-2 text-ink-3 text-xs">No tenants available</div>}
            {clients.map((c) => (
              <button key={c.id}
                      onClick={() => { setActiveTenant(c.id); window.location.reload(); }}
                      className={cn("w-full text-left px-3 py-2 text-xs hover:bg-panel-850",
                                    String(c.id) === current ? "text-accent-deep" : "text-ink-2")}>
                {c.name}
              </button>
            ))}
            {current && (
              <button onClick={() => { setActiveTenant(null); window.location.reload(); }}
                      className="w-full text-left px-3 py-2 text-xs text-ink-3 hover:bg-panel-850 border-t border-panel-700">
                Clear selection
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// User identity + account menu. The chevron now opens a real dropdown
// (identity, role, settings, sign out) instead of logging out on click —
// a down-chevron promises a menu, so it should deliver one.
function UserMenu({ user, isStaff, consoleHref, onLogout }: {
  user: UserOut | null;
  isStaff: boolean;
  consoleHref: string;
  onLogout: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const name = user?.full_name || user?.username || "—";
  const hasSeparateUsername = !!(user?.full_name && user?.username);
  return (
    // min-w-0 here, on the button, and on the name span: flexbox truncation
    // needs it on every link of the chain. Without it this menu holds its 185px
    // min-content, the header's right group cannot compress past it, and the
    // overflow lands on the page instead of on the name.
    <div className="relative min-w-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          // w-full, not just min-w-0: a <button> sizes width:auto by SHRINK-TO-FIT,
          // not by filling its block container, so this one held 185px of content
          // inside a wrapper the header had already squeezed to 156px and hung
          // 13px past the viewport at 1024. w-full makes it follow the wrapper.
          "flex items-center gap-1.5 h-9 w-full min-w-0 pl-2.5 pr-1.5 rounded-md transition-colors",
          // On the dark header: a navy 6% wash on near-black is no feedback at
          // all. Light washes, and the open state borrows SlotNav's --bg-dark.
          open ? "bg-[rgba(255,255,255,0.10)]" : "hover:bg-[rgba(255,255,255,0.07)]",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {/* No ink class: --ink-1 and --ink IS the header background — the same
            hex, 1.00:1. The name inherits the header's own --paper instead, the
            way Logo does. */}
        <span className="font-mono text-xs truncate min-w-0 max-w-[150px]">{name}</span>
        <ChevronDown size={13} className={cn("shrink-0 text-ink-4 transition-transform duration-150", open && "rotate-180")} />
      </button>
      {open && (
        <>
          {/* z > 1000: Leaflet panes/controls reach z-1000 — the atlas page
              swallowed this menu at z-50 (same fix as the tenant switcher). */}
          <div className="fixed inset-0 z-[1090]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-[1100] w-[224px] overflow-hidden py-1"
               style={{
                 background: "var(--paper-2)",
                 border: "1px solid var(--hairline)",
                 borderRadius: 8,
                 boxShadow: "0 10px 30px rgba(10,22,40,0.14), 0 1px 0 rgba(255,255,255,0.7) inset",
               }}>
            <div className="px-3 py-2.5" style={{ borderBottom: "1px solid var(--hairline)" }}>
              <div className="font-mono text-[9px] uppercase tracking-widest2 text-ink-4">Signed in as</div>
              <div className="font-mono text-xs text-ink-1 mt-1 truncate">{name}</div>
              {hasSeparateUsername && (
                <div className="font-mono text-[10px] text-ink-3 truncate">@{user?.username}</div>
              )}
              <div className="mt-2 inline-flex items-center font-mono text-[9px] uppercase tracking-wider text-accent-deep px-1.5 py-px"
                   style={{ border: "1px solid rgba(var(--accent-rgb) / 0.40)", background: "rgba(var(--accent-rgb) / 0.10)", borderRadius: 5 }}>
                {user?.role ?? "guest"}
              </div>
            </div>
            <Link
              to="/reports"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-xs text-ink-2 hover:bg-[rgba(10,22,40,0.05)] transition-colors"
            >
              <FileText size={13} /> Reports
            </Link>
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 text-xs text-ink-2 hover:bg-[rgba(10,22,40,0.05)] transition-colors"
            >
              <Settings size={13} /> Settings
            </Link>
            {isStaff && (
              <a
                href={consoleHref}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3 py-2 text-xs text-ink-2 hover:bg-[rgba(10,22,40,0.05)] transition-colors"
                title="Open the staff Console (operator + admin tools)"
              >
                <Wrench size={13} /> Console
              </a>
            )}
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-status-crit hover:bg-[rgba(192,57,43,0.07)] transition-colors"
              style={{ borderTop: "1px solid var(--hairline)" }}
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Notification bell + unread badge + dropdown center. Exported (alongside
// visibleNav) so its "conversation" → openChatForTenant wiring is directly
// testable without standing up the whole AppShell.
export function NotificationBell({ isStaff }: { isStaff: boolean }): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const countQ = useQuery({
    queryKey: ["notif-count"], queryFn: () => apiClient.notificationsUnread(),
    refetchInterval: 30_000, staleTime: 20_000,
  });
  const listQ = useQuery({
    queryKey: ["notif-list"], queryFn: () => apiClient.notifications(), enabled: open, staleTime: 5_000,
  });
  const count = countQ.data?.count ?? 0;
  const items = listQ.data ?? [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["notif-count"] }); qc.invalidateQueries({ queryKey: ["notif-list"] }); };
  const openItem = async (n: { id: number; entity_type: string; entity_id: number | null }) => {
    await apiClient.notificationRead(n.id).catch(() => undefined);
    setOpen(false); refresh();
    // A งาน notification opens THAT งาน. It used to open /submissions — the
    // bare queue — and leave the reader to find the row again; the workspace
    // has its own route now (2026-09-01), so the id in the notification is
    // finally worth something. Without one, TODAY is the queue.
    if (n.entity_type === "submission") { navigate(n.entity_id ? `/jobs/${n.entity_id}` : "/today"); return; }
    // A new customer message (entity_id = tenant_id) — NotificationBell sits
    // in the header, OUTSIDE <DockProvider>, so it can't open the "chat"
    // window directly; openChatForTenant bridges that via a one-shot
    // localStorage marker + reload that TeamChatDockMounter consumes on its
    // next mount (see TeamChatDock.tsx).
    if (n.entity_type === "conversation") openChatForTenant(isStaff, n.entity_id);
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} className="relative shrink-0 p-1.5 text-ink-4 hover:text-paper transition-colors" aria-label="Notifications">
        <Bell size={16} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center font-mono"
                style={{ minWidth: 15, height: 15, fontSize: 9, fontWeight: 700, color: "var(--ink-1)", background: "var(--accent)", borderRadius: 999, padding: "0 3px" }}>
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[1090]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-[1100] w-[340px] bg-panel-900 border border-panel-700 shadow-2xl">
            <div className="px-3 py-2 flex items-center justify-between border-b border-panel-700">
              <span className="font-mono uppercase text-ink-3" style={{ fontSize: 9.5, letterSpacing: ".16em" }}>Notifications</span>
              {count > 0 && (
                <button onClick={async () => { await apiClient.notificationsReadAll(); refresh(); }} className="font-mono uppercase text-ink-3 hover:text-accent-deep" style={{ fontSize: 8.5, letterSpacing: ".1em" }}>Mark all read</button>
              )}
            </div>
            <div className="max-h-[360px] overflow-auto">
              {listQ.isPending && <div className="px-3 py-6 text-center font-mono text-ink-3" style={{ fontSize: 11 }}>Loading…</div>}
              {!listQ.isPending && items.length === 0 && <div className="px-3 py-6 text-center font-mono text-ink-3" style={{ fontSize: 11 }}>Nothing yet</div>}
              {items.map((n) => (
                <button key={n.id} onClick={() => void openItem(n)}
                        className="w-full text-left px-3 py-2 border-b border-panel-800 hover:bg-panel-850 transition-colors flex gap-2"
                        style={{ opacity: n.read_at ? 0.55 : 1 }}>
                  <span className="mt-1 shrink-0" style={{ width: 6, height: 6, borderRadius: 999, background: n.read_at ? "transparent" : (n.severity === "warn" || n.severity === "crit" ? "var(--warn)" : "var(--accent)") }} />
                  <span className="min-w-0">
                    <span className="block font-mono text-ink-1" style={{ fontSize: 12 }}>{n.message_md}</span>
                    <span className="block font-mono text-ink-4" style={{ fontSize: 9.5 }}>{n.created_at.replace("T", " ").slice(0, 16)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// visibleNav/visibleQuickRow now live in ./shell/navItems.ts — re-exported
// here so existing imports of
// InboxScreen.test.tsx) keep resolving without changes. NAV_SLOTS moved there
// too — it named destinations NAV already owned, and restating them is how the
// two lists came to disagree about who may see /inbox.
// `visibleNav` from "./AppShell" / "@/components/AppShell" (AppShell.test.tsx,
export { visibleNav, NAV_SLOTS } from "./shell/navItems";
export type { NavSlot } from "./shell/navItems";

/** The nav row: the design's seven slots, then the destinations no slot names.
 *  One row, one model — `visibleSlots` and `promotedNav` both read
 *  shell/navItems.ts, so what renders and who may see it cannot drift.
 *
 *  It is now the ONLY nav surface. QuickRow went first (it and SlotNav were two
 *  rows doing one job); MenuBar went on 2026-09-01, once every destination it
 *  uniquely offered had somewhere else to be — /reports being the one that had
 *  blocked it since 2026-08-27. A second surface listing the same destinations
 *  is what let ALERTS be hidden on one and offered on the other. */
export function SlotNav({ isStaff }: { isStaff: boolean }): JSX.Element {
  const location = useLocation();
  const slots = visibleSlots(isStaff);
  const promoted = promotedNav(isStaff);

  function isActive(to: string): boolean {
    // Match the section, not the exact destination: the file room's entry
    // points at /files/queue and must still read as current on /files/assets
    // and /files/packages, which are lenses on the same place. (It rides in
    // the promoted half of the row since 2026-09-01 rather than in a slot —
    // this rule is about the section either way.)
    if (to === "/") return location.pathname === "/";
    const section = `/${to.split("/")[1]}`;
    return location.pathname === section || location.pathname.startsWith(`${section}/`);
  }

  const link = (key: string, to: string, label: string, Icon: LucideIcon, text: string) => (
    <Link
      key={key}
      to={to}
      title={label}
      data-testid={`nav-slot-${key}`}
      className="flex items-center gap-1.5 px-2.5 h-7 font-mono uppercase whitespace-nowrap transition-colors"
      style={{
        fontSize: 10.5,
        letterSpacing: ".11em",
        // --accent-2 (#f0912e), not --accent: the palette move took accent-on-
        // dark from 8.1:1 to 3.86:1, below AA at 10.5px. accent-2 is 7.6:1 on
        // --ink and is what the handoff calls accent-bright.
        color: isActive(to) ? "var(--accent-2)" : "var(--ink-4)",
        borderRadius: 6,
        background: isActive(to) ? "var(--bg-dark)" : "transparent",
      }}
    >
      <Icon size={12} />
      {text}
    </Link>
  );

  return (
    // flexShrink .05 — a twentieth of the right group's 1 — so a tight row takes
    // its width from the search field first and from this row last. The nav
    // keeps every entry at every width.
    //
    // THE INVARIANT: at any width >= 900px the page must never gain a horizontal
    // scrollbar. The nav may scroll inside its own container — that is what
    // overflow-x-auto is for, and it is the behaviour this shell had before the
    // branch. A whole-page sideways scroll is not acceptable degradation; an
    // internal nav scroll is.
    //
    // Measured in Chromium, real markup, built CSS (page overflow / nav shown):
    //   1920 1600 1440 1366 1280   0px   nav 584/584, search field 240px
    //   1279 1240                  0px   nav 584/584, search collapsed to a button
    //   1152                       0px   nav 584/584
    //   1024                       0px   nav scrolls in place (581 of 584)
    //    960                       0px   nav scrolls in place (574 of 584)
    //    900                       0px   nav scrolls in place (568 of 584)
    //
    // jsdom computes no layout, so the suite cannot check any of that; it pins
    // the structural chain instead (see AppShell.test.tsx, "the chain that keeps
    // the header from scrolling the page"). Re-measure rather than assume if the
    // header gains a child.
    <nav aria-label="เมนูหลัก" className="flex items-center gap-0.5 overflow-x-auto"
         style={{ flexShrink: 0.05, minWidth: 0 }}>
      {slots.map((s) => link(s.key, s.to, s.label, s.icon, s.text))}
      {promoted.length > 0 && (
        <span aria-hidden className="mx-1.5 shrink-0"
              style={{ width: 1, height: 14, background: "var(--ink-2)" }} />
      )}
      {promoted.map((i) => link(i.to, i.to, i.label, i.icon, i.label))}
    </nav>
  );
}

// Staff land here with no active tenant (only the Console hand-off sets one),
// so Postgres RLS — which fails closed — would scope the first Atlas fetch to
// nothing. AppShell resolves a default BEFORE mounting page content, so the
// initial request carries X-Tenant-Id and the estate is not silently empty.
//
// The one case that must NOT be resolved is a staff user who cleared the
// selection on purpose. Absence of a tenant cannot tell that apart from never
// having chosen one, so this reads the intent recorded by setActiveTenant.
// Without the second clause, the picker's "Clear selection" re-selected
// clients[0] on the very reload it triggers and changed nothing a user could
// see. With it, the no-tenant states that the file room and LeftTree already
// draw ("เลือกหน่วยงานที่แถบสถานะด้านล่าง", never "no data") become reachable
// for the first time.
export function needsDefaultTenant(isStaff: boolean): boolean {
  return isStaff && !getActiveTenant() && !tenantClearedOnPurpose();
}

export function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const user = getCachedUser();
  const isStaff = !!user && (STAFF_ROLES as readonly string[]).includes(user.role);
  // The header's รอตรวจ badge is gone with the route it was keyed to
  // (2026-09-01). Re-keying it to /today would have been the cheap move and
  // the wrong one: a count of assets awaiting a signature, rendered beside
  // ถาดงานวันนี้, reads as "N things today" and would have been a lie in the
  // one place the header cannot afford one. The count did not disappear — it
  // moved somewhere better. "/" lands on TODAY now, so the รอตรวจ chip and
  // its number are on the first screen after sign-in rather than behind a
  // click-to-open menu panel. TodayScreen owns the ["pending-reviews"] query.
  const narrow = useIsNarrow();
  // The 1600px `tightHeader` breakpoint went with MenuBar — it existed only to
  // collapse that bar to a single button before the nav had to scroll. The
  // header row is that much shorter now; the search field still gives width
  // first (see CommandSearch's inline branch), and the nav still scrolls inside
  // its own container rather than widening the page. RE-MEASURE in Chromium
  // rather than assume: the table on SlotNav below was taken with the menu bar
  // present and is now conservative, not wrong.
  const [treeMobileOpen, setTreeMobileOpen] = useState(false);

  const [tenantReady, setTenantReady] = useState(!needsDefaultTenant(isStaff));
  useEffect(() => {
    if (tenantReady) return;
    let cancelled = false;
    apiClient.clients()
      .then((cs) => {
        if (cancelled) return;
        if (needsDefaultTenant(isStaff) && cs.length > 0) setActiveTenant(cs[0].id);
      })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setTenantReady(true); });
    return () => { cancelled = true; };
  }, [tenantReady, isStaff]);

  function logout(): void {
    clearToken();
    navigate("/login", { replace: true });
  }

  // Build today's UTC stamp once per render (cheap, no SetInterval)
  const utcStamp = new Date().toISOString().slice(0, 19).replace("T", " ");

  if (!tenantReady) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-[11px] uppercase tracking-widest2 text-ink-3">
        Loading workspace…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* ── Top header — 40px, var(--ink): wordmark · nav · search · account ──
          One row now. The quick row that used to sit under this one is gone:
          it and the slot nav were two surfaces doing one job, and every entry
          it carried is either a slot or promoted into the same row (see
          SlotNav). */}
      <header className="relative" style={{ background: "var(--ink)", color: "var(--paper)" }}>
        <div className="flex items-center gap-3 px-4" style={{ height: 40 }}>
          <Link to="/" aria-label="ไปถาดงานวันนี้" className="flex items-center shrink-0">
            <Logo size="sm" />
          </Link>

          <SlotNav isStaff={isStaff} />

          {/* Right — global search + account menu */}
          <div className="ml-auto flex items-center gap-2 min-w-0">
            <CommandSearch inline />
            <NotificationBell isStaff={isStaff} />
            <UserMenu
              user={user}
              isStaff={isStaff}
              consoleHref={studiosHandoffUrl(getToken() ?? "")}
              onLogout={logout}
            />
          </div>
        </div>
      </header>

      {/* ── Page content + unified dock (MODS + team chat) ──────────
          Both windows now live in one shared DockBar instead of each dock
          owning its own fixed corner, so staff can have MODS and the team
          chat open side by side — no more route-based mutual exclusion (the
          old "hide ChatDock on /intake and /submissions so it doesn't
          collide with TeamChatDock's identical corner" guard is gone; the
          window system makes that collision structurally impossible). See
          docs/superpowers/specs/2026-07-21-unified-dock.md Phase 1b. */}
      <WorkspaceProvider>
        <WorklistProvider>
          <DockProvider>
            <div className="flex-1 flex min-h-0">
              {/* Left tree — structural nav (roadmap A1: Plants -> Assets).
                  Desktop: a permanent (collapsible) sidebar. Narrow (<900px,
                  field tablets): replaced by a toggle button; the tree itself
                  renders as a full-height overlay only while opened. */}
              {narrow ? (
                <>
                  <button type="button" onClick={() => setTreeMobileOpen(true)}
                          aria-label="เปิดแผนผังไซต์" data-testid="tree-mobile-toggle"
                          className="shrink-0 px-1.5 border-r hover:bg-[rgba(31,78,121,0.05)]"
                          style={{ borderColor: "var(--hairline)" }}>
                    <PanelLeft size={14} style={{ color: "var(--ink-3)" }} />
                  </button>
                  {treeMobileOpen && <LeftTree narrow onCloseMobile={() => setTreeMobileOpen(false)} />}
                </>
              ) : (
                <LeftTree />
              )}
              <main className="flex-1 relative min-w-0">
                <Outlet />
              </main>
            </div>
            {/* MODS is the staff default on every route now (no more
                /intake or /submissions exclusion). TeamChatDockMounter is
                registered for staff only once an active tenant is set (RLS
                scope) — for customers it mounts unconditionally and defaults
                open (their one always-available surface).
                Customer MODS (Phase 2, spec 2026-07-21-unified-dock.md): a
                customer always has their own tenant (no tenant-select step
                like staff), so ModsDockMounter mounts unconditionally for
                them too — it registers CLOSED by default (see
                ModsDockMounter's isStaff check) and appears as a launcher
                pill beside their chat window. The entitlement gate inside
                ModsBody still handles the premium "Upgrade to unlock"
                screen per tenant, so no extra gating is needed here. */}
            <ModsDockMounter />
            {isStaff && !!getActiveTenant() && <TeamChatDockMounter />}
            {!isStaff && <TeamChatDockMounter />}
            {/* Notes scratchpad (workbench roadmap C3) — staff-only, gated at
                the mount level so the window never even registers for a
                customer session (see NotesDock.tsx's docstring). */}
            {isStaff && <NotesDockMounter />}
            {/* Honest presence (roadmap 2.3) — mounted unconditionally here,
                unlike TeamChatDockMounter's staff+tenant gate: it's cheap to
                mount, and PresenceBeacon's own canBeat() check already
                no-ops a staff caller with no tenant selected yet (same
                "nothing to register presence against" case). AppShell (and
                everything nested inside it here, including DockProvider)
                is the layout element for every authenticated route — none
                of it remounts on navigation, only the <Outlet> content
                does — so this single mount covers every portal page. */}
            <PresenceBeacon />
            <DockBar />
          </DockProvider>
        </WorklistProvider>
      </WorkspaceProvider>

      {/* ── Client validation caveat — engine numbers aren't signed off ── */}
      {!isStaff && <ValidationCaveat />}

      {/* ── Bottom status bar — system + tenant context ──────────── */}
      {/* ── Bottom status strip — 26px: system state, tenant scope, period,
          calc version, clock. Restyled, not rewritten: it already carried all
          of this. "SYS · OPERATIONAL" lost its middle dot so the state reads as
          one phrase, and the two text-brand navies (the last off-palette colour
          in the shell) are now --ok for the live state and --ink-4 for the
          clock. */}
      <div className="shrink-0 flex items-center justify-between px-4 font-mono uppercase"
           style={{ height: 26, fontSize: 10, letterSpacing: ".14em",
                    background: "var(--surface-2)", color: "var(--ink-3)",
                    borderTop: "1px solid var(--hairline)" }}>
        <div className="flex items-center gap-4">
          <span style={{ color: "var(--ok)" }}>● SYS OPERATIONAL</span>
          {isStaff
            ? <TenantSwitcher />
            : <span>TENANT · {user?.full_name?.toUpperCase() ?? user?.username?.toUpperCase() ?? "—"}</span>}
          <span>PERIOD · FY{new Date().getFullYear()}</span>
          <span>CALC · v1.0.0</span>
        </div>
        <div className="flex items-center gap-4">
          <span>{utcStamp} UTC</span>
        </div>
      </div>
    </div>
  );
}
